import React, { useState } from 'react';
import Button from '../common/Button';
import Input from '../common/Input';
import Card from '../common/Card';
import Modal from '../common/Modal';
import ConfirmDialog from '../common/ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';
import { formatPrice, calculateResellerPrice, checkPricingViability } from '../../utils/priceCalculator';
import { formatVariationLabel } from '../../utils/variationLabel';

// Modelo de pricing alinhado com a Documentação Funcional v1.0:
// - cost: custo interno (uso administrativo).
// - price: preço cheio CONSUMER.
// - commissionPercent: percentual de desconto aplicado a RESELLER (0–100).
// - resellerPrice: calculado a partir de price × (1 - commissionPercent/100).
// Trava de prejuízo client-side: bloqueia salvar quando resellerPrice < cost.

const emptyForm = (defaults = {}) => ({
  name: '',
  size: '',
  color: '',
  cost: '',
  price: '',
  commissionPercent: defaults.commissionPercent ?? '',
  stock: ''
});

const VariationManager = ({ variations = [], onChange, defaultCommissionPercent = null }) => {
  const { error } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [toRemoveIndex, setToRemoveIndex] = useState(null);
  const [variationForm, setVariationForm] = useState(() => emptyForm({
    commissionPercent: defaultCommissionPercent != null ? String(defaultCommissionPercent) : ''
  }));

  const openModal = (index = null) => {
    if (index !== null) {
      const variation = variations[index];
      // Aceita tanto forma flat (nova/recém-editada) quanto API (size/color em attributes).
      setVariationForm({
        name: variation.name || '',
        size: variation.size ?? variation.attributes?.size ?? '',
        color: variation.color ?? variation.attributes?.color ?? '',
        cost: variation.cost ?? '',
        price: variation.price ?? '',
        commissionPercent: variation.commissionPercent ?? '',
        stock: variation.stock ?? ''
      });
      setEditingIndex(index);
    } else {
      // Nova variação herda comissão padrão da loja (quando businessType=reseller).
      setVariationForm(emptyForm({
        commissionPercent: defaultCommissionPercent != null ? String(defaultCommissionPercent) : ''
      }));
      setEditingIndex(null);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingIndex(null);
    setVariationForm(emptyForm({
      commissionPercent: defaultCommissionPercent != null ? String(defaultCommissionPercent) : ''
    }));
  };

  const cost = parseFloat(variationForm.cost) || 0;
  const price = parseFloat(variationForm.price) || 0;
  const commissionPercent = parseFloat(variationForm.commissionPercent) || 0;
  const computedResellerPrice = calculateResellerPrice(price, commissionPercent);
  const viability = checkPricingViability({ price, commissionPercent, cost });

  const handleVariationSubmit = (e) => {
    e.preventDefault();
    // Modal usa portal (DOM real), mas React eventos ainda borbulham pela árvore
    // lógica — sem stopPropagation o onSubmit do ProductForm dispara junto.
    e.stopPropagation();

    if (!variationForm.name.trim()) {
      error('Nome da variação é obrigatório');
      return;
    }
    if (price <= 0) {
      error('Preço deve ser maior que zero');
      return;
    }
    if (commissionPercent < 0 || commissionPercent > 100) {
      error('Percentual de comissão deve estar entre 0 e 100');
      return;
    }
    if (!viability.ok) {
      error(viability.message);
      return;
    }

    const stock = parseInt(variationForm.stock, 10) || 0;
    const newVariation = {
      id: editingIndex !== null ? variations[editingIndex].id : `v-${Date.now()}-${Math.random()}`,
      name: variationForm.name.trim(),
      size: variationForm.size.trim(),
      color: variationForm.color.trim(),
      cost,
      price,
      commissionPercent,
      resellerPrice: computedResellerPrice,
      stock
    };

    const updatedVariations = editingIndex !== null
      ? variations.map((v, i) => i === editingIndex ? newVariation : v)
      : [...variations, newVariation];

    onChange(updatedVariations);
    closeModal();
  };

  const duplicateVariation = (index) => {
    const original = variations[index];
    if (!original) return;
    const copy = {
      ...original,
      id: `v-${Date.now()}-${Math.random()}`,
    };
    const next = [
      ...variations.slice(0, index + 1),
      copy,
      ...variations.slice(index + 1),
    ];
    onChange(next);
  };

  const removeVariation = (index) => setToRemoveIndex(index);

  const confirmRemoveVariation = () => {
    if (toRemoveIndex == null) return;
    onChange(variations.filter((_, i) => i !== toRemoveIndex));
    setToRemoveIndex(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setVariationForm(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="space-y-4">
      {variations.length > 0 ? (
        <div className="space-y-2">
          {variations.map((variation, index) => {
            const margin = (variation.resellerPrice ?? 0) - (variation.cost ?? 0);
            return (
              <Card key={variation.id || index} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-semibold text-app-text">{formatVariationLabel(variation)}</div>
                    <div className="text-sm text-app-text-secondary mt-1 grid grid-cols-2 md:grid-cols-5 gap-x-4 gap-y-1">
                      <span>Custo: {formatPrice(variation.cost)}</span>
                      <span>Cliente: {formatPrice(variation.price)}</span>
                      <span>Revendedor: {formatPrice(variation.resellerPrice)}</span>
                      <span>Comissão: {Number(variation.commissionPercent || 0).toFixed(2)}%</span>
                      <span>Estoque: {variation.stock}</span>
                    </div>
                    {margin < 0 && (
                      <div className="mt-1 text-xs text-error font-semibold">
                        ⚠ Prejuízo: revendedor paga menos que o custo
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openModal(index)}>
                      Editar
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => duplicateVariation(index)}>
                      Duplicar
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => removeVariation(index)}>
                      Remover
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-app-text-secondary">
          Nenhuma variação adicionada ainda
        </div>
      )}

      <Button variant="outline" onClick={() => openModal()} fullWidth>
        + Adicionar Variação
      </Button>

      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={editingIndex !== null ? 'Editar Variação' : 'Nova Variação'}
        size="md"
      >
        <form onSubmit={handleVariationSubmit} className="space-y-4">
          <Input
            label="Nome da Variação"
            name="name"
            value={variationForm.name}
            onChange={handleFormChange}
            required
            placeholder="Ex: P - Azul"
            helpText="Nome completo da variação que será exibido"
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Tamanho (opcional)"
              name="size"
              value={variationForm.size}
              onChange={handleFormChange}
              placeholder="Ex: P, M, G"
            />
            <Input
              label="Cor (opcional)"
              name="color"
              value={variationForm.color}
              onChange={handleFormChange}
              placeholder="Ex: Azul, Vermelho"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Input
              label="Custo (R$)"
              name="cost"
              type="number"
              step="0.01"
              min="0"
              value={variationForm.cost}
              onChange={handleFormChange}
              placeholder="0.00"
              helpText="Custo interno (admin)"
            />
            <Input
              label="Preço cliente (R$)"
              name="price"
              type="number"
              step="0.01"
              min="0"
              value={variationForm.price}
              onChange={handleFormChange}
              required
              placeholder="0.00"
              helpText="Preço cheio CONSUMER"
            />
            <Input
              label="Comissão (%)"
              name="commissionPercent"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={variationForm.commissionPercent}
              onChange={handleFormChange}
              placeholder="0"
              helpText="Desconto do revendedor"
            />
          </div>

          <div className={`p-3 rounded-lg border ${viability.ok ? 'bg-success/5 border-success/30' : 'bg-error/5 border-error/30'}`}>
            <div className="text-sm">
              <div className="flex justify-between">
                <span className="text-app-text-secondary">Preço revendedor (calculado):</span>
                <strong className={viability.ok ? 'text-success' : 'text-error'}>
                  {formatPrice(computedResellerPrice)}
                </strong>
              </div>
              {!viability.ok && (
                <div className="mt-2 text-xs text-error font-semibold">
                  {viability.message}
                </div>
              )}
            </div>
          </div>

          <Input
            label="Estoque"
            name="stock"
            type="number"
            min="0"
            value={variationForm.stock}
            onChange={handleFormChange}
            required
            placeholder="0"
          />

          <div className="flex gap-2 mt-6">
            <Button type="button" variant="outline" fullWidth onClick={closeModal}>
              Cancelar
            </Button>
            <Button type="submit" fullWidth disabled={!viability.ok}>
              {editingIndex !== null ? 'Atualizar' : 'Adicionar'}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={toRemoveIndex != null}
        onClose={() => setToRemoveIndex(null)}
        onConfirm={confirmRemoveVariation}
        title="Remover variação?"
        message="Essa variação será apagada da lista."
        confirmLabel="Remover"
        variant="danger"
      />
    </div>
  );
};

export default VariationManager;
